import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Clinic EMR toast confinement (updated 2026-10-13):
 *
 * The Toaster is a `position: fixed` viewport-anchored container rendered
 * from main.tsx (outside AppLayout), so it can't inherit layout context.
 * Requirement (user, 2026-10-13): toasts span the FULL width of the left
 * sidebar — flush against the viewport's left edge AND the sidebar's right
 * edge (240px = the `w-60` sidebar in Sidebar.tsx).
 *
 * Two sonner defaults must be overridden, each via a DIFFERENT mechanism.
 * Sonner 2.0.8 builds the toaster's inline style as
 * `{ --width, --gap, ...style, ...assignOffset(offset, mobileOffset) }`:
 *
 * 1. WIDTH — sonner hardcodes `--width: 356px` inline but spreads user
 *    `style` AFTER its own defaults, so `style={{ "--width": "240px" }}`
 *    wins. Each `[data-sonner-toast]` child gets `width: var(--width)`
 *    from sonner's stylesheet, so it follows automatically.
 *
 * 2. LEFT OFFSET — sonner's default `offset` (24px) becomes
 *    `--offset-left: 24px` via `assignOffset()`, which runs AFTER the
 *    user `style` spread — so `--offset-left` in `style` would be
 *    overwritten. The `offset` prop is the canonical override:
 *    `offset={{ left: 0 }}` pins the toaster's left edge at x=0.
 *
 * The collapsed sidebar (`w-16`, 64px) is too narrow for a readable toast,
 * so the width stays 240px in both sidebar states (readable floor, user
 * ruling 2026-10-05) — toasts extend past the 64px rail by design.
 *
 * If the sidebar width classes (`w-60`) ever change, keep the 240px here
 * in sync (see post-mortem.md, toast confinement entries).
 */
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      offset={{ left: 0 }}
      style={{ "--width": "240px" } as React.CSSProperties}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-cream-100 group-[.toaster]:text-ink group-[.toaster]:border-cream-200 group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-maroon-600 group-[.toast]:text-cream-50",
          cancelButton: "group-[.toast]:bg-cream-200 group-[.toast]:text-maroon-700",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
